import React from 'react';
import { Button, Spin, Switch, message } from 'antd';
import useChartingPreference from 'hooks/useChartingPreference';
import ChartingLayoutPicker from 'components/ChartingLayoutPicker';
import {
    CHARTING_SECTIONS,
    DEFAULT_CHARTING_PREFERENCE,
    TChartingLayout,
    TChartingPreference,
    TChartingSection,
    isSameChartingPreference,
} from 'types/chartingPreference';
import s from './s.module.less';

const LAYOUT_CLASS: Record<TChartingLayout, string> = {
    CHART: s.chart,
    VIDEO: s.video,
    SIDE_BY_SIDE: s.balanced,
    FLOATING: s.floating,
};

// Small wireframe of the session: labeled blocks stand for the sections, a gray block for the video
const Preview = ({ pref }: { pref: TChartingPreference }) => {
    const { sections } = pref;
    const blocks = [
        sections.PRESCRIPTIONS && { key: 'rx', label: 'Prescriptions' },
        { key: 'tx', label: 'Visit note', lines: 2 },
        sections.PRIVATE_NOTES && { key: 'notes', label: 'Private notes' },
        sections.BILLING && { key: 'billingNote', label: 'Billing team notes' },
        sections.DIAGNOSIS && { key: 'dx', label: 'Diagnosis' },
        sections.BILLING && { key: 'billing', label: 'Billing codes' },
    ].filter(Boolean) as { key: string; label: string; lines?: number }[];

    return (
        <div className={s.pvFrame} aria-label="Preview of the session workspace">
            <div className={s.pvTop}>
                <i />
                <i />
                <i />
            </div>
            <div className={`${s.wf} ${LAYOUT_CLASS[pref.layout]}`}>
                <div className={s.wfChart}>
                    {blocks.map((b) => (
                        <div key={b.key} className={`${s.wfS} ${b.lines ? s.tall : ''}`}>
                            {b.label}
                            {Array.from({ length: b.lines || 0 }).map((_, i) => (
                                <i key={i} />
                            ))}
                        </div>
                    ))}
                </div>
                <div className={s.wfVideo}>
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <rect x="2" y="6" width="14" height="12" rx="2" />
                        <path d="m16 10 6-3v10l-6-3z" />
                    </svg>
                    Video
                </div>
            </div>
        </div>
    );
};

const ChartingPreferences = () => {
    const [data, loading, update, reset] = useChartingPreference();
    const [saving, setSaving] = React.useState(false);
    const pref = data?.current || DEFAULT_CHARTING_PREFERENCE;
    const isDefault = !data || isSameChartingPreference(data.current, data.defaults);

    const save = async (run: () => Promise<boolean>, successText: string) => {
        setSaving(true);
        const ok = await run();
        setSaving(false);
        // Request already shows the error message on failure
        if (ok) message.success(successText);
    };

    const handleSectionChange = (section: TChartingSection, checked: boolean) =>
        save(() => update({ sections: { [section]: checked } }), 'Charting preferences saved.');

    const handleLayoutChange = (layout: TChartingLayout) =>
        save(() => update({ layout }), 'Charting preferences saved.');

    const handleRestore = () => save(reset, 'Charting preferences restored to defaults.');

    return (
        <Spin spinning={loading}>
            <div className={s.wrap}>
                <div className={s.header}>
                    <h2 className={s.title}>Charting preferences</h2>
                    <Button onClick={handleRestore} disabled={isDefault || saving}>
                        Restore defaults
                    </Button>
                </div>
                <div className={s.desc}>
                    Set up your charting workspace once. Every session opens this way, for all your patients.
                </div>
                <div className={s.body}>
                    <div className={s.main}>
                        <div className={s.group}>
                            <div className={s.label}>Workspace sections</div>
                            <div className={s.help}>Turn a section off to hide it when a session opens.</div>
                            <div className={s.list}>
                                {CHARTING_SECTIONS.map(({ value, label, desc }) => (
                                    <div key={value} className={s.row}>
                                        <div className={s.rowBody}>
                                            <div className={s.rowTitle}>{label}</div>
                                            {desc && <div className={s.rowDesc}>{desc}</div>}
                                        </div>
                                        <Switch
                                            checked={pref.sections[value]}
                                            disabled={!data || saving}
                                            aria-label={label}
                                            onChange={(checked) => handleSectionChange(value, checked)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={s.side}>
                        <div className={s.group}>
                            <div className={s.label}>Default layout</div>
                            <ChartingLayoutPicker
                                value={pref.layout}
                                onChange={(layout) => {
                                    if (data && !saving) handleLayoutChange(layout);
                                }}
                            />
                        </div>
                        <div className={s.group}>
                            <div className={`${s.label} ${s.pvLabel}`}>
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />
                                    <circle cx="12" cy="12" r="3" />
                                </svg>
                                Preview
                            </div>
                            <Preview pref={pref} />
                        </div>
                    </div>
                </div>
            </div>
        </Spin>
    );
};

export default ChartingPreferences;
